import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  ecosystemRequest,
  type ProgramSummary,
  type ProgramDetail,
  type RewardEntitlement,
  type Challenge,
} from '../../services/api/passportEcosystem';
import { PassportNav } from './PassportHub';
import { useAuth } from '../../contexts/AuthContext';
import './passport-types.css';
import { passportKinds } from '@bilt/domain/passportRegistry';
export function PassportProgramShelf({ compact = false }: { compact?: boolean }) {
  const [search, setSearch] = useSearchParams();
  const location = useLocation();
  const value = (key: string) => compact ? '' : search.get(`program_${key}`) || '';
  const city=value('city'), q=value('q'), kind=value('kind'), from=value('from'), to=value('to'), availability=value('availability');
  const filterKey=JSON.stringify([city,q,kind,from,to,availability]);
  const currentFilter=useRef(filterKey);currentFilter.current=filterKey;
  const setFilter=(key:string,next:string)=>setSearch(previous=>{const updated=new URLSearchParams(window.location.pathname===location.pathname ? window.location.search : previous);if(next)updated.set(`program_${key}`,next);else updated.delete(`program_${key}`);return updated;},{replace:true});
  const [moreBusy,setMoreBusy]=useState(false);
  const [items, setItems] = useState<ProgramSummary[]>([]),
    [error, setError] = useState(''),
    [loading, setLoading] = useState(true),
    [offset, setOffset] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      void ecosystemRequest<{ items: ProgramSummary[]; next_offset: number | null }>(
        'catalog',
        undefined,
        { city, q, kind, from, to, availability }
      )
        .then((r) => {
          if (active) {
            setItems(r.items);
            setOffset(r.next_offset);
            setError('');
          }
        })
        .catch((e) => {
          if (active) setError(e.message);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [city, q, kind, from, to, availability]);
  if (error.includes('not available yet')) return null;
  return (
    <section className="pe-program-shelf">
      <header>
        <div>
          <span className="pc-eyebrow">Your next outing</span>
          <h2>{compact ? 'Explore your next program' : 'Find a program'}</h2>
        </div>
        <Link className="pc-link" to="/passport/rewards">
          Rewards
        </Link>
      </header>
      {!compact && (
        <div className="pc-actions pe-program-filters">
          <label>
            City or area
            <input
              className="pc-filter"
              value={city}
              onChange={(e) => setFilter('city',e.target.value)}
              placeholder="Choose an area"
            />
          </label>
          <label>
            Program name
            <input
              type="search"
              className="pc-filter"
              value={q}
              onChange={(e) => setFilter('q',e.target.value)}
            />
          </label>
          <label>Subject type<select aria-label="Subject type" className="pc-filter" value={kind} onChange={e=>setFilter('kind',e.target.value)}><option value="">All types</option>{Object.keys(passportKinds).map(type=><option key={type} value={type}>{type.charAt(0).toUpperCase()+type.slice(1)}</option>)}</select></label>
          <label>Availability<select aria-label="Availability" className="pc-filter" value={availability} onChange={e=>setFilter('availability',e.target.value)}><option value="">All availability</option>{[['available','Available now'],['upcoming','Upcoming'],['paused','Paused'],['ended','Ended'],['requirements_unavailable','Requirements unavailable'],['benefit_unavailable','Benefit unavailable']].map(([key,label])=><option key={key} value={key}>{label}</option>)}</select></label>
          <label>From date<input className="pc-filter" type="date" value={from} onChange={e=>setFilter('from',e.target.value)} /></label>
          <label>Through date<input className="pc-filter" type="date" value={to} onChange={e=>setFilter('to',e.target.value)} /></label>
          <small>Dates use each program’s published timezone.</small>
        </div>
      )}
      {loading ? (
        <p role="status">Finding available programs…</p>
      ) : error ? (
        <p role="alert">{error}</p>
      ) : items.length === 0 ? (
        <p>
          {offset!==null ? 'No matches in these results. Continue to more programs, or broaden the filters.' : 'No programs match these filters. Try a broader area or date range, or collect a public Bilt subject when you encounter its QR.'}
        </p>
      ) : (
        <div className="pe-program-grid">
          {items.slice(0, compact ? 3 : items.length).map((p) => (
            <article key={p.public_id}>
              <span className="pe-kicker">
                {p.joined
                  ? 'Joined'
                  : p.saved
                    ? 'Saved'
                    : (p.availability || 'available').replaceAll('_', ' ')}
                {p.city ? ` · ${p.city}` : ''}
              </span>
              <h3>
                <Link to={`/passport/programs/${p.public_id}`}>{p.title}</Link>
              </h3>
              <p>{p.instructions}</p>
              <p>{p.next_action}</p>
              {p.benefit_policy === 'claim_limited' && (
                <p>Benefit stock is checked when claimed.</p>
              )}
              {p.starts_at && (
                <p>
                  From{' '}
                  {new Date(p.starts_at).toLocaleDateString(undefined, { timeZone: p.timezone })} ·{' '}
                  {p.timezone}
                </p>
              )}
              <Link className="pc-link" to={`/passport/programs/${p.public_id}`}>
                {p.joined ? 'Continue program' : 'View requirements'}
              </Link>
            </article>
          ))}
        </div>
      )}
      {!compact && offset !== null && (
        <button
          className="pc-button"
          disabled={loading || moreBusy}
          onClick={() => {
            const requestedFilter=filterKey;
            setMoreBusy(true);
            void ecosystemRequest<{items:ProgramSummary[];next_offset:number|null}>('catalog',undefined,{city,q,kind,from,to,availability,offset:String(offset)})
              .then(r=>{if(currentFilter.current!==requestedFilter)return;setItems(previous=>[...previous,...r.items.filter(item=>!previous.some(existing=>existing.public_id===item.public_id))]);setOffset(r.next_offset);})
              .catch(e=>{if(currentFilter.current===requestedFilter)setError(e.message);})
              .finally(()=>setMoreBusy(false));
          }}
        >
          More programs
        </button>
      )}
    </section>
  );
}
export default function PassportProgramPage() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const [program, setProgram] = useState<ProgramDetail | null>(null),
    [error, setError] = useState(''),
    [accepted, setAccepted] = useState(false),
    [busy, setBusy] = useState(false),
    [challenge, setChallenge] = useState<Challenge | null>(null);
  const command = useRef<Record<string, string>>({});
  const [revisionAccepted, setRevisionAccepted] = useState<Record<string, boolean>>({});
  const [sessions, setSessions] = useState<Record<string, string>>({}),
    [methods, setMethods] = useState<Record<string, string>>({});
  const load = () =>
    ecosystemRequest<ProgramDetail>('program', undefined, { id })
      .then(setProgram)
      .catch((e) => setError(e.message));
  useEffect(() => {
    let active = true;
    setProgram(null);
    setChallenge(null);
    setAccepted(false);
    setError('');
    command.current = {};
    setSessions({});
    setMethods({});
    void ecosystemRequest<ProgramDetail>('program', undefined, { id })
      .then((r) => {
        if (active) setProgram(r);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    }; /* account-bound read */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);
  const request = async (subject_ref: string) => {
    if (!program) return;
    setBusy(true);
    try {
      const stop = program.stops.find((s) => s.subject_ref === subject_ref);
      const evidence = methods[subject_ref] || stop?.assistance[0];
      const session_ref = sessions[subject_ref] || null;
      const key = JSON.stringify([subject_ref, evidence, session_ref]);
      command.current[key] ||= crypto.randomUUID();
      setChallenge(
        await ecosystemRequest<Challenge>('challenge', {
          command_id: command.current[key],
          purpose: 'visit',
          version_ref: program.version_ref,
          subject_ref,
          evidence,
          session_ref,
        })
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="pc-page">
      <Link to="/passport" className="pc-link">
        Back to Passport
      </Link>
      <PassportNav active="explore" />
      {error && <p role="alert">{error}</p>}
      {!program && !error ? (
        <p role="status">Loading program…</p>
      ) : (
        program && (
          <>
            <header className="pe-program-heading">
              <span className="pc-eyebrow">
                {program.status} · {program.timezone}
              </span>
              <h1>{program.title}</h1>
              <p>{program.instructions}</p>
            </header>
            {program.progress && (
              <section className="pe-progress">
                <h2>Your progress</h2>
                <progress value={program.progress.progress} max={program.progress.target} />
                <p>
                  {program.progress.progress} of {program.progress.target} · Cycle {program.cycle}
                </p>
                {program.progress.next_eligible_at && (
                  <p>
                    Next eligible after{' '}
                    {new Date(program.progress.next_eligible_at).toLocaleString()}
                  </p>
                )}
              </section>
            )}
            <div className="pe-program-grid">
              {program.stops.map((s, i) => (
                <article key={s.subject_ref}>
                  <span className="pe-kicker">{s.subject.subject_type}</span>
                  <h2>{s.subject.title}</h2>
                  {s.session_required && (
                    <label>
                      Published session
                      <select
                        value={sessions[s.subject_ref] || ''}
                        onChange={(e) =>
                          setSessions({ ...sessions, [s.subject_ref]: e.target.value })
                        }
                      >
                        <option value="">Choose session</option>
                        {s.sessions.map((session) => (
                          <option
                            key={session.public_id}
                            value={session.public_id}
                            disabled={session.status !== 'open'}
                          >
                            {session.title} · {session.status} · {session.timezone}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {s.paused && (
                    <p role="status">
                      This stop is paused. Visits during the pause do not count toward this program.
                    </p>
                  )}
                  {s.assistance?.length > 1 && (
                    <label>
                      Confirmation type
                      <select
                        value={methods[s.subject_ref] || s.assistance[0]}
                        onChange={(e) =>
                          setMethods({ ...methods, [s.subject_ref]: e.target.value })
                        }
                      >
                        {s.assistance.map((m) => (
                          <option value={m} key={m}>
                            {m.replaceAll('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {program.joined && s.assistance?.length > 0 ? (
                    <button
                      className="pc-button"
                      disabled={
                        busy ||
                        program.status !== 'active' ||
                        (s.session_required && !sessions[s.subject_ref])
                      }
                      onClick={() => void request(s.subject_ref)}
                    >
                      Ask staff to confirm
                    </button>
                  ) : (
                    <p>Use this subject’s approved QR and the published earning requirements.</p>
                  )}
                </article>
              ))}
            </div>
            {program.reward && (
              <section>
                <h2>{program.reward.title}</h2>
                <p>
                  {program.reward.stock_policy === 'guaranteed'
                    ? 'Stock is reserved before an eligible cycle starts.'
                    : 'Availability is limited; stock is reserved when claimed.'}
                </p>
                <p>
                  Provided by {program.reward.fulfillment_partner}. Funded by{' '}
                  {program.reward.funding_partner}.
                </p>
                <Link className="pc-link" to="/passport/rewards">
                  Your rewards
                </Link>
              </section>
            )}
            {program.transitions?.map((t) => (
              <section className="pe-program-terms" key={t.public_id}>
                <h2>Revised program available</h2>
                <p>{t.explanation}</p>
                <p>{t.instructions}</p>
                <p>{t.terms}</p>
                <p>
                  Prior evidence is re-evaluated under these requirements. Spent visits and
                  completed benefits remain in their original history.
                </p>
                <label>
                  <input
                    type="checkbox"
                    checked={revisionAccepted[t.public_id] || false}
                    onChange={(e) =>
                      setRevisionAccepted({ ...revisionAccepted, [t.public_id]: e.target.checked })
                    }
                  />
                  I accept the revised participation terms.
                </label>
                <button
                  className="pc-button"
                  disabled={busy || !revisionAccepted[t.public_id]}
                  onClick={() => {
                    setBusy(true);
                    command.current[t.public_id] ||= crypto.randomUUID();
                    void ecosystemRequest('accept_transition', {
                      command_id: command.current[t.public_id],
                      transition_ref: t.public_id,
                      terms_version: t.terms_version,
                    })
                      .then(load)
                      .catch((e) => setError(e.message))
                      .finally(() => setBusy(false));
                  }}
                >
                  Continue with revised program
                </button>
              </section>
            ))}
            <section className="pe-program-terms">
              <h2>Participation</h2>
              <p>
                {program.terms ||
                  'Collectibles do not grant admission, membership, ownership or proof of purchase.'}
              </p>
              {!program.joined && (
                <label>
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                  />{' '}
                  I accept these participation terms.
                </label>
              )}
              <div className="pc-actions">
                {!program.joined ? (
                  <button
                    className="pc-button"
                    disabled={!accepted || busy || program.status !== 'active'}
                    onClick={() => {
                      setBusy(true);
                      void ecosystemRequest('join', {
                        program_ref: id,
                        terms_version: program.terms_version,
                      })
                        .then(load)
                        .catch((e) => setError(e.message))
                        .finally(() => setBusy(false));
                    }}
                  >
                    Join program
                  </button>
                ) : (
                  <button
                    className="pc-link"
                    onClick={() =>
                      void ecosystemRequest('leave', { program_ref: id })
                        .then(load)
                        .catch((e) => setError(e.message))
                    }
                  >
                    Leave program
                  </button>
                )}
                <button
                  className="pc-link"
                  onClick={() =>
                    void ecosystemRequest('save', { program_ref: id, saved: true })
                      .then(() => setError('Program saved. Saving does not join it.'))
                      .catch((e) => setError(e.message))
                  }
                >
                  Save for later
                </button>
              </div>
            </section>
            {challenge && (
              <ChallengeDisplay
                challenge={challenge}
                onClose={() => {
                  setChallenge(null);
                  command.current = {};
                  void load();
                }}
              />
            )}
          </>
        )
      )}
    </main>
  );
}
export function ChallengeDisplay({
  challenge,
  onClose,
}: {
  challenge: Challenge;
  onClose: () => void;
}) {
  const [now, setNow] = useState(Date.now()),
    [state, setState] = useState(challenge.state || 'waiting'),
    [receipt, setReceipt] = useState(
      challenge.receipt?.receipt_ref || challenge.receipt?.visit_ref || ''
    );
  useEffect(() => {
    let active = true;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void ecosystemRequest<{
        state: string;
        receipt: { receipt_ref?: string; visit_ref?: string } | null;
      }>('challenge_status', undefined, { id: challenge.id })
        .then((r) => {
          if (active) {
            setState(r.state);
            setReceipt(r.receipt?.receipt_ref || r.receipt?.visit_ref || '');
          }
        })
        .catch(() => {});
    }, 3000);
    return () => {
      active = false;
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [challenge.id]);
  const remaining = Math.max(
    0,
    Math.ceil((Date.parse(challenge.expires_at.replace(' ', 'T')) - now) / 1000)
  );
  return (
    <section className="pe-challenge" aria-label="Staff confirmation">
      <h2>
        {state === 'confirmed'
          ? 'Confirmed by staff'
          : challenge.purpose === 'redemption'
            ? 'Present to staff'
            : 'Ask staff to scan'}
      </h2>
      {state === 'confirmed' ? (
        <p role="status">The server recorded this confirmation. Receipt {receipt}.</p>
      ) : remaining > 0 && state === 'waiting' && challenge.challenge ? (
        <>
          <div className="pe-qr">
            <QRCodeSVG value={challenge.challenge} size={220} marginSize={4} />
          </div>
          <p>
            Expires in {remaining} seconds. This QR has not yet confirmed a visit or redemption.
          </p>
        </>
      ) : (
        <p>
          {state === 'corrected'
            ? 'This confirmation was corrected. Check your visit history.'
            : state === 'unavailable'
              ? 'This benefit is unavailable or on hold. Check its current reward record.'
              : 'This request expired. Close it and request a new QR.'}
        </p>
      )}
      <button className="pc-button" onClick={onClose}>
        Close
      </button>
    </section>
  );
}
export function PassportRewardsPage() {
  const { user } = useAuth();
  const command = useRef<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<RewardEntitlement[] | null>(null),
    [error, setError] = useState(''),
    [challenge, setChallenge] = useState<Challenge | null>(null);
  const load = () =>
    ecosystemRequest<RewardEntitlement[]>('rewards')
      .then(setItems)
      .catch((e) => setError(e.message));
  useEffect(() => {
    let active = true;
    setItems(null);
    setChallenge(null);
    command.current = {};
    void ecosystemRequest<RewardEntitlement[]>('rewards')
      .then((r) => {
        if (active) setItems(r);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  return (
    <main className="pc-page">
      <PassportNav active="overview" />
      <h1>Your rewards</h1>
      <p>Earned benefits stay here while you make progress toward the next cycle.</p>
      {error && <p role="alert">{error}</p>}
      {items === null && !error ? (
        <p role="status">Loading benefits…</p>
      ) : items?.length === 0 ? (
        <p>
          No benefits earned yet. <Link to="/passport">Return to your Passport</Link>.
        </p>
      ) : (
        <div className="pe-program-grid">
          {items?.map((r) => (
            <article key={r.public_id}>
              <span className="pe-kicker">
                {r.state} · Cycle {r.cycle}
              </span>
              <h2>{r.title}</h2>
              <p>{r.description}</p>
              <p>Provided by {r.fulfillment_partner}</p>
              <p>Expires {new Date(r.expires_at.replace(' ', 'T') + 'Z').toLocaleString()}</p>
              {['reserved', 'eligible'].includes(r.state) && (
                <button
                  className="pc-button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    command.current[r.public_id] ||= crypto.randomUUID();
                    void ecosystemRequest<Challenge>('challenge', {
                      command_id: command.current[r.public_id],
                      purpose: 'redemption',
                      entitlement_ref: r.public_id,
                    })
                      .then(setChallenge)
                      .catch((e) => setError(e.message))
                      .finally(() => setBusy(false));
                  }}
                >
                  Present to staff
                </button>
              )}
            </article>
          ))}
        </div>
      )}
      {challenge && (
        <ChallengeDisplay
          challenge={challenge}
          onClose={() => {
            setChallenge(null);
            command.current = {};
            void load();
          }}
        />
      )}
    </main>
  );
}
type VisitRow = {
  public_id: string;
  occurred_at: string;
  evidence_kind: string;
  status: string;
  session_ref: string | null;
  subject: { title: string } | null;
  qualifications: Array<{ program_ref: string; title: string; cycle: number; status: string }>;
};
export function PassportVisitHistory({
  subjectRef,
  standalone = false,
}: {
  subjectRef?: string;
  standalone?: boolean;
}) {
  const { user } = useAuth();
  const [rows, setRows] = useState<VisitRow[] | null>(null),
    [offset, setOffset] = useState<number | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setRows(null);
    setError('');
    setOffset(null);
    void ecosystemRequest<{ items: VisitRow[]; next_offset: number | null }>('visits', undefined, {
      subject_ref: subjectRef || '',
    })
      .then((r) => {
        if (active) {
          setRows(r.items);
          setOffset(r.next_offset);
        }
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [subjectRef, user?.id]);
  if (error.includes('not available yet')) return null;
  return (
    <section className={standalone ? 'pc-page' : 'pe-visits'}>
      {standalone && <PassportNav active="activity" />}
      <h2>Visit history</h2>
      {error ? (
        <p role="alert">{error}</p>
      ) : rows === null ? (
        <p role="status">Loading visits…</p>
      ) : rows.length === 0 ? (
        <p>
          No accepted visits recorded here yet. A repeat scan counts only when it meets a program’s
          published requirements.
        </p>
      ) : (
        <ol className="pe-journal">
          {rows.map((r) => (
            <li key={r.public_id}>
              <time>{new Date(r.occurred_at.replace(' ', 'T') + 'Z').toLocaleString()}</time>
              <div>
                <strong>{r.subject?.title || 'Private collected memory'}</strong>
                <p>
                  {r.evidence_kind.replaceAll('_', ' ')} · {r.status}
                </p>
                {r.qualifications?.map((q) => (
                  <p key={`${q.program_ref}:${q.cycle}`}>
                    <Link to={`/passport/programs/${q.program_ref}`}>{q.title}</Link> · Cycle{' '}
                    {q.cycle} · {q.status}
                  </p>
                ))}
                {!r.qualifications?.length && (
                  <p>Recorded history; no program credit recorded for this visit.</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      {offset !== null && (
        <button
          className="pc-button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void ecosystemRequest<{ items: VisitRow[]; next_offset: number | null }>(
              'visits',
              undefined,
              { subject_ref: subjectRef || '', offset: String(offset) }
            )
              .then((r) => {
                setRows((current) => [...(current || []), ...r.items]);
                setOffset(r.next_offset);
              })
              .catch((e) => setError(e.message))
              .finally(() => setBusy(false));
          }}
        >
          More visits
        </button>
      )}
    </section>
  );
}
